import { useMemo, useState, type FormEvent } from "react";
import { Cpu, Layers3, Plus, RadioTower } from "lucide-react";
import {
  useAdminDeviceModels,
  useAdminDeviceTypes,
  useCreateDeviceModel,
  useCreateDeviceType,
  type CreateDeviceTypeRequest,
  type UpsertDeviceModelRequest,
} from "../../api/deviceTypes";
import { extractErrorMessage } from "../../api/errors";
import type { DeviceModel, DeviceModelFilesystem, DeviceType, DeviceTypeFieldDefinition } from "../../api/types";
import { DataTable } from "../../components/DataTable";

// rowId keys the row in the list. It must not change while the row is
// edited (a key built from the path remounted the row on every keystroke
// and dropped focus), and it is not sent: sanitizeField leaves it out.
type DraftField = DeviceTypeFieldDefinition & { rowId: number };

let nextRowId = 0;
const newRowId = () => ++nextRowId;

// The API's default resource budget (1.5 MiB), shown as the placeholder.
const DEFAULT_RESOURCE_BUDGET_KB = 1536;

const FILESYSTEM_LABELS: Record<DeviceModelFilesystem, string> = {
  littlefs: "LittleFS",
  spiffs: "SPIFFS",
};

function formatBudget(bytes: number): string {
  const mib = bytes / (1024 * 1024);
  if (mib >= 1) return `${Number.isInteger(mib) ? mib : mib.toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

const emptyField = (): DraftField => ({
  rowId: newRowId(),
  path: "",
  label: "",
  kind: "url",
  required: false,
  section: "",
  description: "",
  placeholder: "",
});

function sanitizeField(field: DraftField): DeviceTypeFieldDefinition {
  return {
    path: field.path.trim(),
    label: field.label.trim(),
    kind: field.kind,
    required: field.required,
    section: field.section?.trim() || undefined,
    description: field.description?.trim() || undefined,
    // A secret field may not carry a placeholder (field definitions are served
    // to every reader), so none is sent for one.
    placeholder: field.kind === "secret" ? undefined : field.placeholder?.trim() || undefined,
  };
}

export function AdminDeviceTypesPage() {
  const deviceTypesQuery = useAdminDeviceTypes();
  const deviceModelsQuery = useAdminDeviceModels();
  const createDeviceType = useCreateDeviceType();
  const createDeviceModel = useCreateDeviceModel();

  const [typeName, setTypeName] = useState("");
  const [typeCode, setTypeCode] = useState("");
  const [typeDescription, setTypeDescription] = useState("");
  const [typeStatus, setTypeStatus] = useState<DeviceType["status"]>("active");
  const [fieldRows, setFieldRows] = useState<DraftField[]>(() => [
    {
      rowId: newRowId(),
      path: "integration.telemetry.primaryUrl",
      label: "Primary API URL",
      kind: "url",
      required: true,
      section: "Integration",
      description: "Primary outbound endpoint for this device family.",
      placeholder: "https://api.example.com/uplink",
    },
  ]);
  const [typeError, setTypeError] = useState("");

  const [modelCode, setModelCode] = useState("");
  const [modelName, setModelName] = useState("");
  const [modelDescription, setModelDescription] = useState("");
  const [modelStatus, setModelStatus] = useState<DeviceModel["status"]>("active");
  const [modelDeviceTypeId, setModelDeviceTypeId] = useState("");
  const [modelFilesystem, setModelFilesystem] = useState<DeviceModelFilesystem>("littlefs");
  // Blank means "use the platform default".
  const [modelBudgetKb, setModelBudgetKb] = useState("");
  const [modelError, setModelError] = useState("");

  const deviceTypes = useMemo(
    () => deviceTypesQuery.data?.data ?? [],
    [deviceTypesQuery.data?.data],
  );
  const deviceModels = useMemo(
    () => deviceModelsQuery.data?.data ?? [],
    [deviceModelsQuery.data?.data],
  );

  const typeColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Device Type",
        sortable: true,
        render: (row: DeviceType) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-white">{row.name}</div>
            <div className="mt-0.5 font-mono text-[11px] text-gray-400 dark:text-riot-navy-400">{row.deviceTypeId}</div>
          </div>
        ),
      },
      { key: "status", header: "Status", sortable: true, render: (row: DeviceType) => <span className="capitalize">{row.status}</span> },
      { key: "schemaVersion", header: "Schema", sortable: true },
      { key: "fields", header: "Fields", render: (row: DeviceType) => row.fields.length },
      {
        key: "description",
        header: "Description",
        render: (row: DeviceType) =>
          row.description ? (
            <span className="text-gray-600 dark:text-riot-navy-300">{row.description}</span>
          ) : (
            <span className="text-gray-400 dark:text-riot-navy-400">-</span>
          ),
      },
    ],
    [],
  );

  const modelColumns = useMemo(
    () => [
      { key: "modelCode", header: "Model Code", sortable: true },
      { key: "name", header: "Name", sortable: true },
      {
        key: "deviceTypeId",
        header: "Device Type",
        sortable: true,
        render: (row: DeviceModel) => {
          const match = deviceTypes.find((type) => type.deviceTypeId === row.deviceTypeId);
          return (
            <div>
              <div className="font-medium text-gray-900 dark:text-white">{match?.name ?? row.deviceTypeId}</div>
              <div className="mt-0.5 font-mono text-[11px] text-gray-400 dark:text-riot-navy-400">{row.deviceTypeId}</div>
            </div>
          );
        },
      },
      { key: "status", header: "Status", sortable: true, render: (row: DeviceModel) => <span className="capitalize">{row.status}</span> },
      {
        key: "filesystem",
        header: "Resources",
        render: (row: DeviceModel) =>
          row.filesystem ? (
            <span className="text-gray-700 dark:text-riot-navy-200">
              {FILESYSTEM_LABELS[row.filesystem] ?? row.filesystem}
              {row.resourceBudgetBytes ? ` · ${formatBudget(row.resourceBudgetBytes)}` : ""}
            </span>
          ) : (
            <span className="text-gray-400 dark:text-riot-navy-400">-</span>
          ),
      },
    ],
    [deviceTypes],
  );

  async function handleCreateType(e: FormEvent) {
    e.preventDefault();
    setTypeError("");

    const fields = fieldRows
      .map(sanitizeField)
      .filter((field) => field.path.length > 0 && field.label.length > 0);

    if (fields.length === 0) {
      setTypeError("Add at least one configuration field for the device type.");
      return;
    }

    const payload: CreateDeviceTypeRequest = {
      code: typeCode.trim(),
      name: typeName.trim(),
      description: typeDescription.trim() || undefined,
      status: typeStatus,
      fields,
    };

    try {
      await createDeviceType.mutateAsync(payload);
      setTypeName("");
      setTypeCode("");
      setTypeDescription("");
      setTypeStatus("active");
      setFieldRows([emptyField()]);
    } catch (err) {
      setTypeError(extractErrorMessage(err, "Failed to create device type"));
    }
  }

  async function handleCreateModel(e: FormEvent) {
    e.preventDefault();
    setModelError("");

    const budgetText = modelBudgetKb.trim();
    const budgetKb = Number(budgetText);
    if (budgetText !== "" && (!Number.isInteger(budgetKb) || budgetKb <= 0)) {
      setModelError("Resource budget must be a whole number of KB, or blank for the default.");
      return;
    }

    const payload: UpsertDeviceModelRequest = {
      modelCode: modelCode.trim(),
      name: modelName.trim(),
      deviceTypeId: modelDeviceTypeId,
      description: modelDescription.trim() || undefined,
      status: modelStatus,
      filesystem: modelFilesystem,
      resourceBudgetBytes: budgetText === "" ? undefined : budgetKb * 1024,
    };

    try {
      await createDeviceModel.mutateAsync(payload);
      setModelCode("");
      setModelName("");
      setModelDescription("");
      setModelStatus("active");
      setModelDeviceTypeId("");
      setModelFilesystem("littlefs");
      setModelBudgetKb("");
    } catch (err) {
      setModelError(extractErrorMessage(err, "Failed to create device model"));
    }
  }

  function updateFieldRow(index: number, patch: Partial<DraftField>) {
    setFieldRows((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  }

  function removeFieldRow(index: number) {
    setFieldRows((rows) => (rows.length === 1 ? rows : rows.filter((_, rowIndex) => rowIndex !== index)));
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-riot-gold-100 text-riot-gold-600 dark:bg-riot-gold-500/10 dark:text-riot-gold-400">
                <Cpu size={22} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-riot-gold-600 dark:text-riot-gold-400">
                  Platform device catalog
                </p>
                <h1 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  Define Riot device families and the schemas they require
                </h1>
                <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-riot-navy-300">
                  Device types define the configuration experience. Device models map real hardware to those types so
                  device onboarding, config validation, and future firmware compatibility all speak the same language.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Device types</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">{deviceTypes.length}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Models</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">{deviceModels.length}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Purpose</div>
                <div className="text-gray-700 dark:text-riot-navy-100">Drive onboarding and config validation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr,1fr]">
        <form onSubmit={(e) => void handleCreateType(e)} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <Layers3 size={17} className="text-riot-gold-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Create device type</h2>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="device-type-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Device type name
              </label>
              <input
                id="device-type-name"
                type="text"
                value={typeName}
                onChange={(e) => setTypeName(e.target.value)}
                placeholder="Two-way Communication Device"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="device-type-code" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Device type code
              </label>
              <input
                id="device-type-code"
                type="text"
                value={typeCode}
                onChange={(e) => setTypeCode(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                placeholder="two_way_comms"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="device-type-description" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Device type description
              </label>
              <textarea
                id="device-type-description"
                value={typeDescription}
                onChange={(e) => setTypeDescription(e.target.value)}
                rows={2}
                placeholder="What this family is for and which runtime settings matter most."
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="device-type-status" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Device type status
              </label>
              <select
                id="device-type-status"
                value={typeStatus}
                onChange={(e) => setTypeStatus(e.target.value as DeviceType["status"])}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-riot-navy-300">Configuration fields</h3>
              <button
                type="button"
                onClick={() => setFieldRows((rows) => [...rows, emptyField()])}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
              >
                <Plus size={13} /> Add field
              </button>
            </div>

            {fieldRows.map((field, index) => (
              <div key={field.rowId} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div>
                    <label htmlFor={`field-path-${index}`} className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-riot-navy-300">
                      Path
                    </label>
                    <input
                      id={`field-path-${index}`}
                      type="text"
                      value={field.path}
                      onChange={(e) => updateFieldRow(index, { path: e.target.value })}
                      placeholder="integration.telemetry.primaryUrl"
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor={`field-label-${index}`} className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-riot-navy-300">
                      Field label
                    </label>
                    <input
                      id={`field-label-${index}`}
                      type="text"
                      value={field.label}
                      onChange={(e) => updateFieldRow(index, { label: e.target.value })}
                      placeholder="Primary API URL"
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor={`field-kind-${index}`} className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-riot-navy-300">
                      Field kind
                    </label>
                    <select
                      id={`field-kind-${index}`}
                      value={field.kind}
                      onChange={(e) => updateFieldRow(index, { kind: e.target.value as DraftField["kind"] })}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                    >
                      <option value="url">URL</option>
                      <option value="phone">Phone</option>
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="string_array">List of strings</option>
                      <option value="phone_array">Phone number list</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="secret">Secret (password)</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`field-section-${index}`} className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-riot-navy-300">
                      Field section
                    </label>
                    <input
                      id={`field-section-${index}`}
                      type="text"
                      value={field.section ?? ""}
                      onChange={(e) => updateFieldRow(index, { section: e.target.value })}
                      placeholder="Integration"
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label htmlFor={`field-help-${index}`} className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-riot-navy-300">
                      Help text
                    </label>
                    <input
                      id={`field-help-${index}`}
                      type="text"
                      value={field.description ?? ""}
                      onChange={(e) => updateFieldRow(index, { description: e.target.value })}
                      placeholder="Explain what the field controls on the device."
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                    />
                  </div>
                  {field.kind !== "secret" && (
                    <div>
                      <label htmlFor={`field-placeholder-${index}`} className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-riot-navy-300">
                        Placeholder
                      </label>
                      <input
                        id={`field-placeholder-${index}`}
                        type="text"
                        value={field.placeholder ?? ""}
                        onChange={(e) => updateFieldRow(index, { placeholder: e.target.value })}
                        placeholder="https://api.example.com/uplink"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white"
                      />
                    </div>
                  )}
                  <div className="flex items-end justify-between">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-riot-navy-200">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateFieldRow(index, { required: e.target.checked })}
                        className="rounded border-gray-300 text-riot-gold-500 focus:ring-riot-gold-500"
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={() => removeFieldRow(index)}
                      className="text-xs text-red-600 hover:underline dark:text-red-400"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {typeError && <p className="mt-4 text-xs text-red-600 dark:text-red-400">{typeError}</p>}

          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={createDeviceType.isPending || !typeName.trim() || !typeCode.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-60"
            >
              <Plus size={14} />
              {createDeviceType.isPending ? "Creating..." : "Create device type"}
            </button>
          </div>
        </form>

        <form onSubmit={(e) => void handleCreateModel(e)} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <RadioTower size={17} className="text-riot-gold-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Create device model</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label htmlFor="model-device-type" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Model device type
              </label>
              <select
                id="model-device-type"
                value={modelDeviceTypeId}
                onChange={(e) => setModelDeviceTypeId(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                <option value="">Select a device type</option>
                {deviceTypes.map((type) => (
                  <option key={type.deviceTypeId} value={type.deviceTypeId}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="model-code" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Model code
              </label>
              <input
                id="model-code"
                type="text"
                value={modelCode}
                onChange={(e) => setModelCode(e.target.value)}
                placeholder="RI-2-Way-Comms-v1"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="model-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Model name
              </label>
              <input
                id="model-name"
                type="text"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="RI 2-Way Comms v1"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="model-status" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Model status
              </label>
              <select
                id="model-status"
                value={modelStatus}
                onChange={(e) => setModelStatus(e.target.value as DeviceModel["status"])}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="model-filesystem" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                  Filesystem
                </label>
                <select
                  id="model-filesystem"
                  value={modelFilesystem}
                  onChange={(e) => setModelFilesystem(e.target.value as DeviceModelFilesystem)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                >
                  <option value="littlefs">LittleFS</option>
                  <option value="spiffs">SPIFFS</option>
                </select>
              </div>
              <div>
                <label htmlFor="model-budget" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                  Resource budget (KB)
                </label>
                <input
                  id="model-budget"
                  type="number"
                  inputMode="numeric"
                  value={modelBudgetKb}
                  onChange={(e) => setModelBudgetKb(e.target.value)}
                  placeholder={`${DEFAULT_RESOURCE_BUDGET_KB} (default)`}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                />
              </div>
              <p className="text-[11px] text-gray-500 sm:col-span-2 dark:text-riot-navy-400">
                The filesystem the model&apos;s firmware mounts, and how much of it resource bundles (web UI files etc.) may use.
                Leave the budget blank for the default.
              </p>
            </div>
            <div>
              <label htmlFor="model-description" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Model description
              </label>
              <textarea
                id="model-description"
                value={modelDescription}
                onChange={(e) => setModelDescription(e.target.value)}
                rows={3}
                placeholder="Optional hardware/firmware notes."
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
          </div>

          {modelError && <p className="mt-4 text-xs text-red-600 dark:text-red-400">{modelError}</p>}

          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={createDeviceModel.isPending || !modelCode.trim() || !modelName.trim() || !modelDeviceTypeId}
              className="inline-flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-60"
            >
              <Plus size={14} />
              {createDeviceModel.isPending ? "Creating..." : "Create model"}
            </button>
          </div>
        </form>
      </div>

      <DataTable
        title="Device types"
        totalCount={deviceTypesQuery.data?.pagination.count ?? deviceTypes.length}
        columns={typeColumns}
        data={deviceTypes}
        keyExtractor={(row) => row.deviceTypeId}
      />

      <DataTable
        title="Device models"
        totalCount={deviceModelsQuery.data?.pagination.count ?? deviceModels.length}
        columns={modelColumns}
        data={deviceModels}
        keyExtractor={(row) => row.modelCode}
      />
    </div>
  );
}
